var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { getQueryParameter, RoomChangedMessage } from "flavr-common";
import { Material, Mesh, MeshBasicMaterial, MeshLambertMaterial, MeshPhongMaterial, MeshStandardMaterial } from "three";
import { mainScene } from "../mainScene";
import { RendererSystem } from "../renderer/RendererSystem";
import { capabilitiesService } from "../CapabilitiesService";
import { preferencesService } from "../../preferences/PreferencesService";
import { onMeshLoadedDelegate } from "../mesh/MeshRenderable";
export var MaterialQuality;
(function (MaterialQuality) {
    MaterialQuality[MaterialQuality["LOW"] = 0] = "LOW";
    MaterialQuality[MaterialQuality["MIDDLE"] = 1] = "MIDDLE";
    MaterialQuality[MaterialQuality["HIGH"] = 2] = "HIGH";
})(MaterialQuality || (MaterialQuality = {}));
export class MaterialQualitySystem extends RendererSystem {
    constructor(rendererModule) {
        super(rendererModule, "MaterialQualitySystem");
        this.materialQuality = MaterialQuality.HIGH;
        this.shadowUpdateTime = 5;
        this.standardMaterialMap = new Map();
        this.hideWhenQualityRenderables = new Set();
        this.onActionDelegate = new Map();
        this.handleRoomChangedMessage = () => {
            this.standardMaterialMap.clear();
            this.changeMeshMaterialsInSceneGraph();
        };
        this.handleMeshLoaded = (meshRenderable) => {
            if (this.materialQuality === MaterialQuality.HIGH)
                return;
            const group = meshRenderable.meshInstance.group;
            if (group == null)
                return;
            group.traverse((node) => {
                if (!(node instanceof Mesh))
                    return;
                this.storeStandardMaterial(node.material, node.uuid);
                const stored = this.standardMaterialMap.get(node.uuid);
                if (stored != null && !Array.isArray(stored) && !(node.material instanceof MeshBasicMaterial)) {
                    node.material = this.convertMaterial(stored, this.materialQuality);
                    node.material.needsUpdate = true;
                }
            });
        };
        let qualityStr = getQueryParameter(window.location.href, "quality").toLowerCase();
        if (!qualityStr) {
            // Auf MobileVR-Geräten (Meta Quest etc.) gespeicherte Qualitätseinstellungen ignorieren,
            // damit die Hardware-Erkennung im default-Zweig immer greift.
            if (!capabilitiesService.isMobileVR) {
                qualityStr = preferencesService.getPreferredValue("materialQuality", "");
            }
        }
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "materialQuality")
                return false;
            this.switchMaterialMode(newValue);
            return true;
        }));
        this.renderer.getConnection().subscribeByType(RoomChangedMessage, this.handleRoomChangedMessage);
        onMeshLoadedDelegate.add(this.handleMeshLoaded);
        this.switchMaterialMode(qualityStr);
    }
    add(hideWhenQualityRenderable) {
        this.hideWhenQualityRenderables.add(hideWhenQualityRenderable);
    }
    delete(hideWhenQualityRenderable) {
        this.hideWhenQualityRenderables.delete(hideWhenQualityRenderable);
    }
    switchMaterialMode(qualityStr) {
        switch (qualityStr) {
            case "0":
            case "low":
                this.materialQuality = MaterialQuality.LOW;
                this.shadowUpdateTime = undefined;
                preferencesService.updatePreferences([{ key: "useDynamicLights", value: "off" }]);
                preferencesService.updatePreferences([{ key: "ambientLightingBrightness", value: 1.2 }]);
                break;
            case "1":
            case "middle":
                this.materialQuality = MaterialQuality.MIDDLE;
                this.shadowUpdateTime = 100;
                const lightsOn = preferencesService.getPreferredValue("useDynamicLights", "") === "on";
                if (lightsOn)
                    preferencesService.updatePreferences([{ key: "ambientLightingBrightness", value: 1 }]);
                else
                    preferencesService.updatePreferences([{ key: "ambientLightingBrightness", value: 2 }]);
                break;
            case "2":
            case "high":
                this.materialQuality = MaterialQuality.HIGH;
                this.shadowUpdateTime = 5;
                preferencesService.updatePreferences([{ key: "useDynamicLights", value: "on" }]);
                preferencesService.updatePreferences([{ key: "ambientLightingBrightness", value: 0.8 }]);
                break;
            default:
                if (capabilitiesService.isMobileVR || capabilitiesService.isMobile() || capabilitiesService.isTablet) {
                    this.materialQuality = MaterialQuality.LOW;
                    this.shadowUpdateTime = undefined;
                    preferencesService.updatePreferences([{ key: "useDynamicLights", value: "off" }]);
                    preferencesService.updatePreferences([{ key: "ambientLightingBrightness", value: 1.2 }]);
                }
                else if (capabilitiesService.isLowEndGPU) {
                    // Automatisch LOW-Qualität für erkannte schwache Grafikkarten
                    this.materialQuality = MaterialQuality.LOW;
                    this.shadowUpdateTime = undefined;
                    // Einstellungen persistent speichern, damit Folgequeries denselben Pfad nehmen
                    preferencesService.updatePreferences([{ key: "materialQuality", value: "low" }]);
                    preferencesService.updatePreferences([{ key: "useDynamicLights", value: "off" }]);
                    preferencesService.updatePreferences([{ key: "ambientLightingBrightness", value: 1.2 }]);
                    // Schatten ebenfalls deaktivieren (type "3" = disabled)
                    preferencesService.updatePreferences([{ key: "shadowMapType", value: "3" }]);
                    // Webcam-Hintergrundunschärfe deaktivieren (MediaPipe-Inferenz ist zu teuer)
                    preferencesService.updatePreferences([
                        { key: "useWebcamBlur", value: false },
                        { key: "webcamBackgroundMode", value: "original" }
                    ]);
                    // Auto-Resolution aggressiver einstellen: schneller reduzieren, langsamer erhöhen
                    preferencesService.updatePreferences([{ key: "framerateThresholdLow", value: 40 }]);
                    preferencesService.updatePreferences([{ key: "framerateThresholdHigh", value: 60 }]);
                    preferencesService.updatePreferences([{ key: "changeRate", value: 0.2 }]);
                }
                else {
                    // Normaler Desktop – HIGH-Qualität als Standard
                    this.materialQuality = MaterialQuality.HIGH;
                    this.shadowUpdateTime = 5;
                    preferencesService.updatePreferences([{ key: "useDynamicLights", value: "on" }]);
                    preferencesService.updatePreferences([{ key: "ambientLightingBrightness", value: 0.8 }]);
                }
                break;
        }
        this.changeMeshMaterialsInSceneGraph();
    }
    saveMeshStandardMaterialsFromScene() {
        mainScene.traverse((node) => {
            if (node instanceof Mesh) {
                this.storeStandardMaterial(node.material, node.uuid);
            }
        });
    }
    changeMeshMaterialsInSceneGraph() {
        this.saveMeshStandardMaterialsFromScene();
        mainScene.traverse((node) => {
            if (node instanceof Mesh) {
                const material = this.standardMaterialMap.get(node.uuid);
                if (Array.isArray(material)) {
                    for (const m of material) {
                        if (!(node.material instanceof MeshBasicMaterial) || (Array.isArray(node.material) && !(node.material[0] instanceof MeshBasicMaterial))) {
                            node.material = this.convertMaterial(m, this.materialQuality);
                            node.material.needsUpdate = true;
                        }
                    }
                }
                else if (material) {
                    if (!(node.material instanceof MeshBasicMaterial)) {
                        node.material = this.convertMaterial(material, this.materialQuality);
                        node.material.needsUpdate = true;
                    }
                }
            }
        });
    }
    storeStandardMaterial(source, uuid) {
        if (this.standardMaterialMap.get(uuid) === undefined && source instanceof MeshStandardMaterial)
            this.standardMaterialMap.set(uuid, source);
    }
    convertMaterial(source, quality) {
        if (!(source instanceof MeshStandardMaterial)) {
            return source;
        }
        switch (quality) {
            case MaterialQuality.LOW:
                return LOWMeshLambertMaterial.fromMeshStandardMaterial(source);
            case MaterialQuality.MIDDLE:
                return MIDDLEMeshPhongMaterial.fromMeshStandardMaterial(source);
        }
        return source;
    }
}
export class LOWMeshLambertMaterial extends MeshLambertMaterial {
    static fromMeshStandardMaterial(source) {
        const material = new LOWMeshLambertMaterial();
        Material.prototype.copy.call(material, source);
        material.color.copy(source.color);
        material.map = source.map;
        material.lightMap = source.lightMap;
        material.lightMapIntensity = source.lightMapIntensity;
        material.aoMap = source.aoMap;
        material.aoMapIntensity = source.aoMapIntensity;
        material.alphaMap = source.alphaMap;
        // material.envMap = source.envMap;
        material.reflectivity = source.metalness;
        material.wireframe = source.wireframe;
        material.wireframeLinewidth = source.wireframeLinewidth;
        material.wireframeLinecap = source.wireframeLinecap;
        material.wireframeLinejoin = source.wireframeLinejoin;
        return material;
    }
    constructor(_a = {}) {
        var rest = __rest(_a, []);
        super(rest);
    }
    copy(source) {
        super.copy(source);
        return this;
    }
}
export class MIDDLEMeshPhongMaterial extends MeshPhongMaterial {
    static fromMeshStandardMaterial(source) {
        const material = new MIDDLEMeshPhongMaterial();
        Material.prototype.copy.call(material, source);
        material.color.copy(source.color);
        material.map = source.map;
        material.lightMap = source.lightMap;
        material.lightMapIntensity = source.lightMapIntensity;
        material.aoMap = source.aoMap;
        material.aoMapIntensity = source.aoMapIntensity;
        material.emissive.copy(source.emissive);
        material.emissiveMap = source.emissiveMap;
        material.emissiveIntensity = source.emissiveIntensity;
        material.normalMapType = source.normalMapType;
        material.normalMap = source.normalMap;
        material.normalScale.copy(source.normalScale);
        material.bumpMap = source.bumpMap;
        material.bumpScale = source.bumpScale;
        material.displacementMap = source.displacementMap;
        material.displacementScale = source.displacementScale;
        material.displacementBias = source.displacementBias;
        material.alphaMap = source.alphaMap;
        // material.envMap = source.envMap;
        material.reflectivity = source.metalness;
        material.wireframe = source.wireframe;
        material.wireframeLinewidth = source.wireframeLinewidth;
        material.wireframeLinecap = source.wireframeLinecap;
        material.wireframeLinejoin = source.wireframeLinejoin;
        return material;
    }
    constructor(_a = {}) {
        var rest = __rest(_a, []);
        super(rest);
    }
    copy(source) {
        super.copy(source);
        return this;
    }
}
